import {
  Box,
  Typography,
  Button,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Fab,
  useTheme,
  alpha,
  Divider,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  RoomService,
  AccessTime,
  Close,
  TrendingUp,
  CurrencyPound
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GET_ALL_SERVICES } from '../graphql/queries';
import { CREATE_SERVICE, UPDATE_SERVICE, DELETE_SERVICE } from '../graphql/mutations';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ConfirmationModal, AlertModal } from '../components/common/ConfirmationModal';
import { CustomSnackbar } from '../components/common/CustomSnackbar';
import { useConfirmation } from '../hooks/useConfirmation';
import { formatGBP, formatGBPShort, calculateAverage } from '../utils/currency';

interface ServiceFormData {
  name: string;
  duration: number;
  price: number;
  isActive: boolean;
}

const Services = () => {
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const storeId = paramStoreId || authStoreId;
  const theme = useTheme();

  // Custom confirmation hook
  const {
    confirmationState,
    alertState,
    snackbarState,
    confirmDelete,
    closeConfirmation,
    closeAlert,
    handleConfirm,
    showSuccessSnackbar,
    showErrorSnackbar,
    closeSnackbar,
  } = useConfirmation();

  // Update localStorage when storeId from URL changes
  useEffect(() => {
    if (paramStoreId) {
      localStorage.setItem('storeId', paramStoreId);
    }
  }, [paramStoreId]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    duration: 30,
    price: 0,
    isActive: true,
  });

  const { data: servicesData, loading: servicesLoading, refetch } = useQuery(
    GET_ALL_SERVICES,
    {
      variables: { storeId },
      skip: !storeId,
    }
  );

  const [createService] = useMutation(CREATE_SERVICE);
  const [updateService] = useMutation(UPDATE_SERVICE);
  const [deleteService] = useMutation(DELETE_SERVICE);

  const services = servicesData?.allServices || [];

  // Calculate active services count
  const activeServices = services.filter((service: any) => service.isActive);

  const handleOpenDialog = (service?: any) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || '',
        duration: service.duration || 30,
        price: service.price || 0,
        isActive: service.isActive !== undefined ? service.isActive : true,
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        duration: 30,
        price: 0,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingService) {
        const result = await updateService({
          variables: {
            serviceId: editingService.id,
            ...formData,
          },
        });
        
        if (result.data?.updateService?.ok) {
          showSuccessSnackbar('Service updated successfully!');
          refetch();
          handleCloseDialog();
        } else {
          const errors = result.data?.updateService?.errors || ['Unknown error occurred'];
          showErrorSnackbar(errors.join(', '));
        }
      } else {
        const result = await createService({
          variables: {
            storeId,
            ...formData,
          },
        });
        
        if (result.data?.createService?.ok) {
          showSuccessSnackbar('Service created successfully!');
          refetch();
          handleCloseDialog();
        } else {
          const errors = result.data?.createService?.errors || ['Unknown error occurred'];
          showErrorSnackbar(errors.join(', '));
        }
      }
    } catch (error) {
      console.error('Error saving service:', error);
      showErrorSnackbar('Failed to save service. Please try again.');
    }
  };

  const handleDelete = async (serviceId: string) => {
    confirmDelete('this service', async () => {
      try {
        const result = await deleteService({
          variables: { serviceId },
        });
        
        if (result.data?.deleteService?.ok) {
          refetch();
          showSuccessSnackbar('Service deleted successfully!');
        } else {
          const errors = result.data?.deleteService?.errors || ['Unknown error occurred'];
          showErrorSnackbar(errors.join(', '));
        }
      } catch (error) {
        console.error('Error deleting service:', error);
        showErrorSnackbar('Failed to delete service. Please try again.');
      }
    });
  };

  if (servicesLoading) {
    return <LoadingSpinner message="Loading services..." />;
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RoomService sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 0.5 }}>
                Services
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                Manage your barbershop services and pricing
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              px: 3,
              py: 1.5,
            }}
          >
            Add Service
          </Button>
        </Box>

        {/* Services Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper sx={{ 
                p: 2.5,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease-in-out',
                '&:hover': { 
                  boxShadow: theme.shadows[2],
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RoomService sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Total Services
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {services.length}
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Paper sx={{ 
                p: 2.5,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease-in-out',
                '&:hover': { 
                  boxShadow: theme.shadows[2],
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CurrencyPound sx={{ color: theme.palette.success.main, mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Avg. Price
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: theme.palette.success.main }}>
                    {formatGBPShort(calculateAverage(activeServices, 'price'))}
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Paper sx={{ 
                p: 2.5,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease-in-out',
                '&:hover': { 
                  boxShadow: theme.shadows[2],
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTime sx={{ color: theme.palette.warning.main, mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Avg. Duration
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: theme.palette.warning.main }}>
                    {activeServices.length > 0 ? Math.round(activeServices.reduce((sum: number, service: any) => sum + service.duration, 0) / activeServices.length) : '0'}m
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Paper sx={{ 
                p: 2.5,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease-in-out',
                '&:hover': { 
                  boxShadow: theme.shadows[2],
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUp sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Active
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} color="secondary">
                    {activeServices.length}
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {services.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Paper sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed'
            }}>
              <Box sx={{ 
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                display: 'inline-block',
                mb: 3
              }}>
                <RoomService sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                No services yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                Create your first service to start managing your barbershop offerings. Add pricing, duration, and descriptions to attract customers.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  boxShadow: theme.shadows[2]
                }}
              >
                Add Your First Service
              </Button>
            </Paper>
          </motion.div>
        ) : (
          <Grid container spacing={3}>
            {services.map((service: any, index: number) => (
              <Grid item xs={12} sm={6} lg={4} key={service.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Paper sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: theme.shadows[1],
                    overflow: 'hidden',
                    transition: 'all 0.3s ease-in-out',
                    opacity: service.isActive ? 1 : 0.6,
                    '&:hover': { 
                      boxShadow: theme.shadows[3],
                      transform: 'translateY(-4px)',
                      opacity: 1
                    }
                  }}>
                    <Box sx={{ 
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: 1,
                      borderColor: 'divider'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ 
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            mr: 2
                          }}>
                            <RoomService fontSize="small" />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="h2" fontWeight={600}>
                              {service.name}
                            </Typography>
                            <Chip
                              label={service.isActive ? "Active" : "Inactive"}
                              size="small"
                              color={service.isActive ? "success" : "default"}
                              sx={{ 
                                fontWeight: 600,
                                borderRadius: 1.5,
                                fontSize: '0.7rem',
                                height: 20,
                                ...(service.isActive ? {} : { color: 'text.secondary' })
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Service">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(service)}
                              sx={{ 
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: theme.palette.primary.main, color: 'white' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Service">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(service.id)}
                              sx={{ 
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: theme.palette.error.main, color: 'white' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    
                    <CardContent sx={{ p: 0 }}>
                      {/* Main Content Area */}
                      <Box sx={{ p: 3, pb: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Duration and Rate Info */}
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ 
                              flex: 1,
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 1.5,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              bgcolor: 'background.default'
                            }}>
                              <AccessTime sx={{ color: theme.palette.warning.main, mr: 1.5 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Duration
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {service.duration} minutes
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              p: 1.5,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              bgcolor: 'background.default'
                            }}>
                              <CurrencyPound sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Per minute
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {service.duration > 0 ? `${formatGBP(service.price / service.duration)}/min` : 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* Price Footer */}
                      <Box sx={{ 
                        p: 2.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderTop: 1,
                        borderColor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Total Price
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="primary">
                          {formatGBPShort(service.price)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            boxShadow: theme.shadows[3],
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease-in-out'
          }}
          onClick={() => handleOpenDialog()}
        >
          <Add />
        </Fab>
      </motion.div>

      {/* Service Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                p: 1,
                borderRadius: 1.5,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                mr: 2
              }}>
                <RoomService fontSize="small" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" fontWeight={600}>
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </Typography>
                {editingService && (
                  <Chip
                    label={formData.isActive ? "Active" : "Inactive"}
                    color={formData.isActive ? "success" : "default"}
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
            <IconButton 
              onClick={handleCloseDialog}
              sx={{ 
                bgcolor: 'background.default',
                '&:hover': { bgcolor: theme.palette.error.main, color: 'white' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RoomService color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price (£)"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyPound color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    Service Active
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
            
            {formData.duration > 0 && formData.price > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: 1,
                  borderColor: alpha(theme.palette.info.main, 0.2)
                }}>
                  <Typography variant="body2" color="info.main" fontWeight={600}>
                    💡 Rate: {formatGBP(formData.price / formData.duration)} per minute
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={!formData.name || !formData.duration || formData.price < 0}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: theme.shadows[2]
            }}
          >
            {editingService ? 'Update Service' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={confirmationState.open}
        onClose={closeConfirmation}
        onConfirm={handleConfirm}
        title={confirmationState.options.title}
        message={confirmationState.options.message}
        confirmText={confirmationState.options.confirmText}
        cancelText={confirmationState.options.cancelText}
        variant={confirmationState.options.variant}
        confirmButtonColor={confirmationState.options.confirmButtonColor}
      />

      {/* Alert Modal */}
      <AlertModal
        open={alertState.open}
        onClose={closeAlert}
        title={alertState.options.title}
        message={alertState.options.message}
        confirmText={alertState.options.confirmText}
        variant={alertState.options.variant}
      />

      {/* Custom Snackbar */}
      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />
    </Box>
  );
};

export default Services;
